import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Users, Star, CheckCircle, XCircle, Clock, LogOut, Eye, 
  BarChart3, Menu, X, Shield, TrendingUp, CreditCard, Video,
  Calendar, Settings, Megaphone, FileCheck, Crown, Plus,
  Edit, Trash2, ExternalLink, MousePointerClick, Image, Upload, Loader2,
  UserCog, Key, ToggleLeft, ToggleRight, Search
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_talentmeet-app/artifacts/w1exeb5m_IMG-20260127-WA0008.jpg";

const MEMBERSHIP_PLANS = {
  basic: { name: "Basic", price: 0, period: "forever" },
  premium: { name: "Premium", price: 999, period: "year" },
  platinum: { name: "Platinum", price: 4999, period: "year" },
  promoter: { name: "Promoter", price: 500000, period: "3 years" }
};

const AD_PLACEMENTS = ["homepage", "discovery", "dashboard", "sidebar"];
const AD_FORMATS = ["top_bar", "banner", "sidebar", "featured"];

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [profiles, setProfiles] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [detailedAnalytics, setDetailedAnalytics] = useState(null);
  const [ratings, setRatings] = useState([]);
  const [users, setUsers] = useState([]);
  const [advertisements, setAdvertisements] = useState([]);
  const [adAnalytics, setAdAnalytics] = useState(null);
  const [content, setContent] = useState([]);
  const [subAdmins, setSubAdmins] = useState([]);
  const [taxonomy, setTaxonomy] = useState({ items: [], grouped: {} });
  const [announcements, setAnnouncements] = useState([]);
  const [announcementCounts, setAnnouncementCounts] = useState({ pending: 0, published: 0, rejected: 0 });
  const [announcementFilter, setAnnouncementFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [uploadingImage, setUploadingImage] = useState(false);
  const adImageRef = useRef(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Platform Settings
  const [platformSettings, setPlatformSettings] = useState({
    whatsapp_number: "",
    whatsapp_enabled: true,
    whatsapp_default_message: "Hello TalentMeet Support, I need assistance.",
    support_email: "",
    custom_domain: ""
  });
  const [savingSettings, setSavingSettings] = useState(false);
  
  // Universal search for all tabs
  const [searchTerm, setSearchTerm] = useState("");
  
  // Taxonomy editing
  const [editingTaxonomy, setEditingTaxonomy] = useState(null);
  const [newTaxonomyItem, setNewTaxonomyItem] = useState({ type: "category", value: "" });
  
  // Dialogs
  const [profileDialog, setProfileDialog] = useState({ open: false, action: null, profile: null, reason: "" });
  const [subscriptionDialog, setSubscriptionDialog] = useState({ open: false, user: null, plan: "", expiresAt: "", note: "", isComplimentary: false });
  const [adDialog, setAdDialog] = useState({ open: false, ad: null, mode: "create" });
  const [contentDialog, setContentDialog] = useState({ open: false, content: null, action: null, reason: "" });
  const [passwordDialog, setPasswordDialog] = useState({ open: false, currentPassword: "", newPassword: "", confirmPassword: "" });
  const [userActionDialog, setUserActionDialog] = useState({ open: false, user: null, action: null, newPassword: "" });
  const [createUserDialog, setCreateUserDialog] = useState({ 
    open: false, 
    email: "", 
    password: "", 
    name: "", 
    role: "talent" 
  });
  const [subAdminDialog, setSubAdminDialog] = useState({ 
    open: false, 
    mode: "create", 
    subAdmin: null,
    formData: {
      email: "",
      password: "",
      name: "",
      permissions: {
        user_management: false,
        profile_verification: false,
        content_approval: false,
        subscription_management: false,
        ad_management: false,
        view_analytics: false,
        view_ratings: false
      }
    }
  });

  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      setUser(JSON.parse(storedUser));
    }
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };

      const [profilesRes, analyticsRes, ratingsRes, usersRes, adsRes, adAnalyticsRes, contentRes, detailedRes, subAdminsRes, taxonomyRes, announcementsRes, settingsRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/profiles`, { headers }),
        fetch(`${API_URL}/api/admin/analytics`, { headers }),
        fetch(`${API_URL}/api/admin/ratings`, { headers }),
        fetch(`${API_URL}/api/admin/users`, { headers }),
        fetch(`${API_URL}/api/admin/advertisements`, { headers }),
        fetch(`${API_URL}/api/admin/advertisements/analytics`, { headers }),
        fetch(`${API_URL}/api/admin/content`, { headers }),
        fetch(`${API_URL}/api/admin/analytics/detailed`, { headers }),
        fetch(`${API_URL}/api/admin/sub-admins`, { headers }),
        fetch(`${API_URL}/api/taxonomy?status=all`, { headers }),
        fetch(`${API_URL}/api/admin/announcements`, { headers }),
        fetch(`${API_URL}/api/admin/platform-settings`, { headers })
      ]);

      if (profilesRes.ok) setProfiles((await profilesRes.json()).profiles);
      if (analyticsRes.ok) setAnalytics(await analyticsRes.json());
      if (ratingsRes.ok) setRatings((await ratingsRes.json()).ratings);
      if (usersRes.ok) setUsers((await usersRes.json()).users);
      if (adsRes.ok) setAdvertisements((await adsRes.json()).advertisements);
      if (adAnalyticsRes.ok) setAdAnalytics(await adAnalyticsRes.json());
      if (contentRes.ok) setContent((await contentRes.json()).content);
      if (detailedRes.ok) setDetailedAnalytics(await detailedRes.json());
      if (subAdminsRes.ok) setSubAdmins((await subAdminsRes.json()).sub_admins);
      if (taxonomyRes.ok) setTaxonomy(await taxonomyRes.json());
      if (announcementsRes.ok) {
        const annData = await announcementsRes.json();
        setAnnouncements(annData.announcements || []);
        setAnnouncementCounts(annData.counts || { pending: 0, published: 0, rejected: 0 });
      }
      if (settingsRes.ok) {
        const settingsData = await settingsRes.json();
        setPlatformSettings({
          whatsapp_number: settingsData.whatsapp_number || "",
          whatsapp_enabled: settingsData.whatsapp_enabled !== false,
          whatsapp_default_message: settingsData.whatsapp_default_message || "Hello TalentMeet Support, I need assistance.",
          support_email: settingsData.support_email || "",
          custom_domain: settingsData.custom_domain || ""
        });
      }
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  // Taxonomy management functions
  const handleAddTaxonomy = async () => {
    if (!newTaxonomyItem.value.trim()) return;
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_URL}/api/taxonomy/add`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify(newTaxonomyItem)
      });
      if (response.ok) {
        toast.success(`Added ${newTaxonomyItem.value}`);
        setNewTaxonomyItem({ type: "category", value: "" });
        fetchData();
      } else {
        const data = await response.json();
        toast.error(data.detail || "Failed to add");
      }
    } catch (error) {
      toast.error("Failed to add taxonomy item");
    }
  };

  const handleUpdateTaxonomy = async (taxonomyId, updates) => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_URL}/api/admin/taxonomy/${taxonomyId}`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify(updates)
      });
      if (response.ok) {
        toast.success("Updated");
        setEditingTaxonomy(null);
        fetchData();
      } else {
        toast.error("Failed to update");
      }
    } catch (error) {
      toast.error("Failed to update");
    }
  };

  const handleDeleteTaxonomy = async (taxonomyId) => {
    if (!confirm("Delete this item?")) return;
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_URL}/api/admin/taxonomy/${taxonomyId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        toast.success("Deleted");
        fetchData();
      }
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  // Announcement management functions
  const handlePublishAnnouncement = async (announcementId) => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_URL}/api/admin/announcements/${announcementId}/publish`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        toast.success("Announcement published");
        fetchData();
      } else {
        toast.error("Failed to publish");
      }
    } catch (error) {
      toast.error("Failed to publish");
    }
  };

  const handleUnpublishAnnouncement = async (announcementId) => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_URL}/api/admin/announcements/${announcementId}/unpublish`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        toast.success("Announcement unpublished");
        fetchData();
      } else {
        toast.error("Failed to unpublish");
      }
    } catch (error) {
      toast.error("Failed to unpublish");
    }
  };

  const handleDeleteAnnouncement = async (announcementId) => {
    if (!confirm("Delete this announcement?")) return;
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_URL}/api/admin/announcements/${announcementId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        toast.success("Announcement deleted");
        fetchData();
      } else {
        toast.error("Failed to delete");
      }
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  const filteredAnnouncements = announcements.filter(ann => 
    announcementFilter === "all" || ann.status === announcementFilter
  );

  // Save Platform Settings
  const handleSavePlatformSettings = async () => {
    setSavingSettings(true);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API_URL}/api/admin/platform-settings`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(platformSettings)
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success("Settings saved successfully!");
        if (data.settings) {
          setPlatformSettings({
            whatsapp_number: data.settings.whatsapp_number || "",
            whatsapp_enabled: data.settings.whatsapp_enabled !== false,
            whatsapp_default_message: data.settings.whatsapp_default_message || "",
            support_email: data.settings.support_email || "",
            custom_domain: data.settings.custom_domain || ""
          });
        }
      } else {
        const error = await res.json();
        toast.error(error.detail || "Failed to save settings");
      }
    } catch (error) {
      toast.error("Failed to save settings");
    } finally {
      setSavingSettings(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    navigate("/");
    toast.success("Logged out");
  };

  // Admin Password Change
  const handlePasswordChange = async () => {
    const { currentPassword, newPassword, confirmPassword } = passwordDialog;
    
    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    
    if (newPassword.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_URL}/api/admin/change-password`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ current_password: currentPassword, new_password: newPassword })
      });
      
      if (response.ok) {
        toast.success("Password changed successfully");
        setPasswordDialog({ open: false, currentPassword: "", newPassword: "", confirmPassword: "" });
      } else {
        const data = await response.json();
        toast.error(data.detail || "Failed to change password");
      }
    } catch (error) {
      toast.error("Failed to change password");
    }
  };

  // User Management Actions
  const handleUserAction = async () => {
    const { user: targetUser, action, newPassword } = userActionDialog;
    const token = localStorage.getItem("token");
    
    try {
      let endpoint = `/api/admin/users/${targetUser.user_id}`;
      let method = "PUT";
      let body = {};
      
      if (action === "delete") {
        method = "DELETE";
      } else if (action === "suspend") {
        endpoint += "/suspend";
      } else if (action === "unsuspend") {
        endpoint += "/unsuspend";
      } else if (action === "reset_password") {
        endpoint += "/reset-password";
        method = "POST";
        body = { new_password: newPassword };
      }
      
      const response = await fetch(`${API_URL}${endpoint}`, {
        method,
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: Object.keys(body).length > 0 ? JSON.stringify(body) : undefined
      });
      
      if (response.ok) {
        toast.success(`User ${action.replace("_", " ")} successful`);
        fetchData();
      } else {
        const data = await response.json();
        toast.error(data.detail || "Action failed");
      }
    } catch (error) {
      toast.error("Action failed");
    }
    setUserActionDialog({ open: false, user: null, action: null, newPassword: "" });
  };

  // Create User
  const handleCreateUser = async () => {
    const { email, password, name, role } = createUserDialog;
    
    if (!email || !password || !name) {
      toast.error("All fields are required");
      return;
    }
    
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_URL}/api/admin/users`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ email, password, name, role })
      });
      
      if (response.ok) {
        toast.success("User created successfully");
        setCreateUserDialog({ open: false, email: "", password: "", name: "", role: "talent" });
        fetchData();
      } else {
        const data = await response.json();
        toast.error(data.detail || "Failed to create user");
      }
    } catch (error) {
      toast.error("Failed to create user");
    }
  };

  // Profile Verification Actions
  const handleProfileAction = async () => {
    const { action, profile, reason } = profileDialog;
    const token = localStorage.getItem("token");
    
    try {
      let endpoint = `/api/admin/profiles/${profile.profile_id}`;
      let body = {};
      
      if (action === "verify") {
        endpoint += "/verify";
      } else if (action === "reject") {
        endpoint += "/reject";
        body = { reason };
      } else if (action === "revoke") {
        endpoint += "/revoke";
        body = { reason };
      }
      
      const response = await fetch(`${API_URL}${endpoint}`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify(body)
      });
      
      if (response.ok) {
        toast.success(`Profile ${action}d successfully`);
        fetchData();
      } else {
        toast.error("Action failed");
      }
    } catch (error) {
      toast.error("Action failed");
    }
    setProfileDialog({ open: false, action: null, profile: null, reason: "" });
  };

  // Subscription Management
  const handleSubscriptionUpdate = async () => {
    const { user: targetUser, plan, expiresAt, note, isComplimentary } = subscriptionDialog;
    const token = localStorage.getItem("token");
    
    try {
      const response = await fetch(`${API_URL}/api/admin/users/${targetUser.user_id}/subscription`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ plan, expires_at: expiresAt, admin_note: note, is_complimentary: isComplimentary })
      });
      
      if (response.ok) {
        toast.success("Subscription updated");
        fetchData();
      } else {
        toast.error("Update failed");
      }
    } catch (error) {
      toast.error("Update failed");
    }
    setSubscriptionDialog({ open: false, user: null, plan: "", expiresAt: "", note: "", isComplimentary: false });
  };

  // Advertisement Management
  const handleAdSave = async () => {
    const { ad, mode } = adDialog;
    const token = localStorage.getItem("token");
    
    try {
      const endpoint = mode === "create" ? `/api/admin/advertisements` : `/api/admin/advertisements/${ad.ad_id}`;
      const method = mode === "create" ? "POST" : "PUT";
      
      const response = await fetch(`${API_URL}${endpoint}`, {
        method,
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify(ad)
      });
      
      if (response.ok) {
        toast.success(mode === "create" ? "Ad created" : "Ad updated");
        fetchData();
      } else {
        toast.error("Save failed");
      }
    } catch (error) {
      toast.error("Save failed");
    }
    setAdDialog({ open: false, ad: null, mode: "create" });
  };

  const handleAdDelete = async (adId) => {
    const token = localStorage.getItem("token");
    try {
      const response = await fetch(`${API_URL}/api/admin/advertisements/${adId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        toast.success("Ad deleted");
        fetchData();
      }
    } catch (error) {
      toast.error("Delete failed");
    }
  };

  // Content Approval
  const handleContentAction = async () => {
    const { content: item, action, reason } = contentDialog;
    const token = localStorage.getItem("token");
    
    try {
      const endpoint = `/api/admin/content/${item.content_id}/${action}`;
      const body = action === "reject" ? { reason } : {};
      
      const response = await fetch(`${API_URL}${endpoint}`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify(body)
      });
      
      if (response.ok) {
        toast.success(`Content ${action}d`);
        fetchData();
      }
    } catch (error) {
      toast.error("Action failed");
    }
    setContentDialog({ open: false, content: null, action: null, reason: "" });
  };

  // Sub-Admin Management
  const handleSubAdminSave = async () => {
    const { mode, formData } = subAdminDialog;
    const token = localStorage.getItem("token");
    
    try {
      if (mode === "create") {
        const response = await fetch(`${API_URL}/api/admin/sub-admins`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
          body: JSON.stringify(formData)
        });
        
        if (response.ok) {
          toast.success("Sub-Admin created successfully");
          fetchData();
        } else {
          const err = await response.json();
          toast.error(err.detail || "Failed to create sub-admin");
        }
      } else {
        const response = await fetch(`${API_URL}/api/admin/sub-admins/${subAdminDialog.subAdmin.user_id}`, {
          method: "PUT",
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
          body: JSON.stringify({
            name: formData.name,
            permissions: formData.permissions,
            is_active: formData.is_active
          })
        });
        
        if (response.ok) {
          toast.success("Sub-Admin updated successfully");
          fetchData();
        } else {
          toast.error("Failed to update sub-admin");
        }
      }
    } catch (error) {
      toast.error("Operation failed");
    }
    setSubAdminDialog({ ...subAdminDialog, open: false });
  };

  const handleSubAdminDelete = async (userId) => {
    if (!window.confirm("Are you sure you want to delete this sub-admin?")) return;
    
    const token = localStorage.getItem("token");
    try {
      const response = await fetch(`${API_URL}/api/admin/sub-admins/${userId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        toast.success("Sub-Admin deleted");
        fetchData();
      }
    } catch (error) {
      toast.error("Delete failed");
    }
  };

  const openCreateSubAdminDialog = () => {
    setSubAdminDialog({
      open: true,
      mode: "create",
      subAdmin: null,
      formData: {
        email: "",
        password: "",
        name: "",
        permissions: {
          user_management: false,
          profile_verification: false,
          content_approval: false,
          subscription_management: false,
          ad_management: false,
          view_analytics: false,
          view_ratings: false
        }
      }
    });
  };

  const openEditSubAdminDialog = (subAdmin) => {
    setSubAdminDialog({
      open: true,
      mode: "edit",
      subAdmin: subAdmin,
      formData: {
        name: subAdmin.name,
        permissions: subAdmin.permissions || {
          user_management: false,
          profile_verification: false,
          content_approval: false,
          subscription_management: false,
          ad_management: false,
          view_analytics: false,
          view_ratings: false
        },
        is_active: subAdmin.is_active !== false
      }
    });
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: "bg-yellow-100 text-yellow-800",
      approved: "bg-green-100 text-green-800",
      verified: "bg-blue-100 text-blue-800",
      rejected: "bg-red-100 text-red-800",
      revoked: "bg-gray-100 text-gray-800",
      suspended: "bg-red-100 text-red-800"
    };
    return <Badge className={`${styles[status] || styles.pending} text-xs`}>{status}</Badge>;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin w-8 h-8 border-4 border-violet-600 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between h-14">
            <div className="flex items-center gap-3">
              <img src={LOGO_URL} alt="TalentMeet" className="h-8 w-8 rounded-lg" />
              <div>
                <span className="font-display text-lg font-bold text-gray-900">TalentMeet</span>
                <Badge className="ml-2 bg-red-100 text-red-700 text-xs">Admin</Badge>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-sm text-gray-600 hidden sm:block">{user?.name}</span>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => setPasswordDialog({ ...passwordDialog, open: true })}
                className="hidden sm:flex items-center gap-1 text-gray-600"
              >
                <Key className="w-4 h-4" />
                <span className="text-xs">Change Password</span>
              </Button>
              <Button variant="ghost" size="icon" onClick={handleLogout} data-testid="logout-btn">
                <LogOut className="w-5 h-5" />
              </Button>
              <Button variant="ghost" size="icon" className="md:hidden" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
                {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6 flex-wrap">
            <TabsTrigger value="overview" data-testid="tab-overview">
              <BarChart3 className="w-4 h-4 mr-2" />Overview
            </TabsTrigger>
            <TabsTrigger value="profiles" data-testid="tab-profiles">
              <Users className="w-4 h-4 mr-2" />Profiles
            </TabsTrigger>
            <TabsTrigger value="users" data-testid="tab-users">
              <UserCog className="w-4 h-4 mr-2" />Users
            </TabsTrigger>
            <TabsTrigger value="content" data-testid="tab-content">
              <FileCheck className="w-4 h-4 mr-2" />Content
            </TabsTrigger>
            <TabsTrigger value="subscriptions" data-testid="tab-subscriptions">
              <Crown className="w-4 h-4 mr-2" />Subs
            </TabsTrigger>
            <TabsTrigger value="advertisements" data-testid="tab-advertisements">
              <Megaphone className="w-4 h-4 mr-2" />Ads
            </TabsTrigger>
            <TabsTrigger value="taxonomy" data-testid="tab-taxonomy">
              <FileCheck className="w-4 h-4 mr-2" />Taxonomy
            </TabsTrigger>
            <TabsTrigger value="announcements" data-testid="tab-announcements">
              <Megaphone className="w-4 h-4 mr-2" />Announcements
              {announcementCounts.pending > 0 && (
                <Badge variant="destructive" className="ml-1 text-xs">{announcementCounts.pending}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="subadmins" data-testid="tab-subadmins">
              <Key className="w-4 h-4 mr-2" />Sub-Admins
            </TabsTrigger>
            <TabsTrigger value="settings" data-testid="tab-settings">
              <Settings className="w-4 h-4 mr-2" />Settings
            </TabsTrigger>
          </TabsList>

          {/* Universal Search Bar */}
          {activeTab !== "overview" && activeTab !== "settings" && (
            <div className="mb-4">
              <div className="relative max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder={`Search ${activeTab}...`}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="admin-search-input"
                />
                {searchTerm && (
                  <button 
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    onClick={() => setSearchTerm("")}
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Overview Tab */}
          <TabsContent value="overview">
            <div className="grid md:grid-cols-5 gap-4 mb-6">
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Total Users</p>
                      <p className="text-2xl font-bold">{analytics?.total_users || 0}</p>
                    </div>
                    <Users className="w-8 h-8 text-violet-500" />
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Total Profiles</p>
                      <p className="text-2xl font-bold">{analytics?.total_profiles || 0}</p>
                    </div>
                    <Users className="w-8 h-8 text-blue-500" />
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Pending Verification</p>
                      <p className="text-2xl font-bold">{detailedAnalytics?.profile_verification?.pending || 0}</p>
                    </div>
                    <Clock className="w-8 h-8 text-yellow-500" />
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Verified Profiles</p>
                      <p className="text-2xl font-bold">{detailedAnalytics?.profile_verification?.verified || 0}</p>
                    </div>
                    <CheckCircle className="w-8 h-8 text-green-500" />
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Est. Revenue</p>
                      <p className="text-2xl font-bold">₹{(detailedAnalytics?.revenue?.estimated_annual || 0).toLocaleString()}</p>
                    </div>
                    <TrendingUp className="w-8 h-8 text-pink-500" />
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {/* Subscription Distribution */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">Subscription Distribution</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {Object.entries(detailedAnalytics?.user_stats?.subscription_distribution || {}).map(([plan, count]) => (
                      <div key={plan} className="flex items-center justify-between">
                        <span className="capitalize text-sm">{MEMBERSHIP_PLANS[plan]?.name || plan}</span>
                        <Badge variant="outline">{count} users</Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Ad Performance */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">Advertisement Performance</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                      <p className="text-2xl font-bold text-violet-600">{adAnalytics?.total_ads || 0}</p>
                      <p className="text-xs text-gray-500">Total Ads</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-blue-600">{adAnalytics?.total_impressions || 0}</p>
                      <p className="text-xs text-gray-500">Impressions</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-green-600">{adAnalytics?.overall_ctr || 0}%</p>
                      <p className="text-xs text-gray-500">CTR</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Content Approval Stats */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">Content Approval</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                      <p className="text-2xl font-bold text-yellow-600">{detailedAnalytics?.content_approval?.pending || 0}</p>
                      <p className="text-xs text-gray-500">Pending</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-green-600">{detailedAnalytics?.content_approval?.approved || 0}</p>
                      <p className="text-xs text-gray-500">Approved</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-red-600">{detailedAnalytics?.content_approval?.rejected || 0}</p>
                      <p className="text-xs text-gray-500">Rejected</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Users by Role */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">Users by Role</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {Object.entries(analytics?.users_by_role || {}).map(([role, count]) => (
                      <div key={role} className="flex items-center justify-between">
                        <span className="capitalize text-sm">{role}</span>
                        <Badge variant="outline">{count}</Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Profiles Tab - Verification */}
          <TabsContent value="profiles">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Profile Verification ({profiles.length} total)</CardTitle>
                <p className="text-sm text-gray-500">Approve, reject, or revoke profile verification</p>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>Verification</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {profiles
                      .filter(p => {
                        if (!searchTerm) return true;
                        const term = searchTerm.toLowerCase();
                        return (
                          (p.personal_info?.full_name || "").toLowerCase().includes(term) ||
                          (p.personal_info?.display_name || "").toLowerCase().includes(term) ||
                          (p.contact_info?.email || "").toLowerCase().includes(term) ||
                          (p.professional_info?.primary_category || "").toLowerCase().includes(term) ||
                          (p.profile_id || "").toLowerCase().includes(term) ||
                          (p.user_role || "").toLowerCase().includes(term)
                        );
                      })
                      .map(profile => (
                      <TableRow key={profile.profile_id}>
                        <TableCell>
                          <div>
                            <p className="font-medium">{profile.personal_info?.full_name || profile.personal_info?.display_name || "N/A"}</p>
                            <p className="text-xs text-gray-400">{profile.profile_id?.slice(0, 15)}...</p>
                          </div>
                        </TableCell>
                        <TableCell className="text-sm">{profile.contact_info?.email || "N/A"}</TableCell>
                        <TableCell>{profile.professional_info?.primary_category || "N/A"}</TableCell>
                        <TableCell><Badge variant="outline" className="capitalize">{profile.user_role || "talent"}</Badge></TableCell>
                        <TableCell>{getStatusBadge(profile.verification_status || "pending")}</TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button size="sm" variant="outline" className="h-7 px-2 text-green-600" 
                              onClick={() => setProfileDialog({ open: true, action: "verify", profile, reason: "" })}
                              disabled={profile.verification_status === "verified"}>
                              <CheckCircle className="w-3 h-3 mr-1" />Verify
                            </Button>
                            <Button size="sm" variant="outline" className="h-7 px-2 text-red-600"
                              onClick={() => setProfileDialog({ open: true, action: "reject", profile, reason: "" })}>
                              <XCircle className="w-3 h-3 mr-1" />Reject
                            </Button>
                            {profile.verification_status === "verified" && (
                              <Button size="sm" variant="outline" className="h-7 px-2 text-gray-600"
                                onClick={() => setProfileDialog({ open: true, action: "revoke", profile, reason: "" })}>
                                Revoke
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Content Tab */}
          <TabsContent value="content">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Content Approval (Platinum Services)</CardTitle>
                <p className="text-sm text-gray-500">Approve casting calls, meetings, auditions, live classes from Platinum members</p>
              </CardHeader>
              <CardContent>
                {content.length === 0 ? (
                  <p className="text-center text-gray-500 py-8">No content pending approval</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Title</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Creator</TableHead>
                        <TableHead>Membership</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {content.map(item => (
                        <TableRow key={item.content_id}>
                          <TableCell>
                            <div>
                              <p className="font-medium">{item.title}</p>
                              {item.rejection_reason && (
                                <p className="text-xs text-red-500 mt-1">Reason: {item.rejection_reason}</p>
                              )}
                            </div>
                          </TableCell>
                          <TableCell><Badge variant="outline">{item.content_type?.replace("_", " ")}</Badge></TableCell>
                          <TableCell>
                            <div>
                              <p className="text-sm">{item.creator_name}</p>
                              <p className="text-xs text-gray-500">{item.creator_email}</p>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge className={
                              item.creator_membership === "platinum" ? "bg-purple-100 text-purple-800" :
                              item.creator_membership === "promoter" ? "bg-yellow-100 text-yellow-800" :
                              "bg-gray-100 text-gray-800"
                            }>
                              {item.creator_membership || "free"}
                            </Badge>
                          </TableCell>
                          <TableCell>{getStatusBadge(item.approval_status)}</TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              <Button size="sm" variant="outline" className="h-7 px-2 text-green-600"
                                onClick={() => setContentDialog({ open: true, content: item, action: "approve", reason: "" })}
                                disabled={item.approval_status === "approved"}>
                                Approve
                              </Button>
                              <Button size="sm" variant="outline" className="h-7 px-2 text-red-600"
                                onClick={() => setContentDialog({ open: true, content: item, action: "reject", reason: "" })}>
                                Reject
                              </Button>
                              {item.approval_status === "approved" && (
                                <Button size="sm" variant="outline" className="h-7 px-2 text-gray-600"
                                  onClick={async () => {
                                    const token = localStorage.getItem("token");
                                    await fetch(`${API_URL}/api/admin/content/${item.content_id}/disable`, {
                                      method: "PUT",
                                      headers: { Authorization: `Bearer ${token}` }
                                    });
                                    toast.success("Content disabled");
                                    fetchData();
                                  }}>
                                  Disable
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="text-base">User Management ({users.length} total)</CardTitle>
                  <p className="text-sm text-gray-500">Create, edit, suspend, or delete users</p>
                </div>
                <Button size="sm" onClick={() => setCreateUserDialog({ ...createUserDialog, open: true })}>
                  <Plus className="w-4 h-4 mr-1" />Create User
                </Button>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>Membership</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {users
                      .filter(u => u.role !== "admin")
                      .filter(u => {
                        if (!searchTerm) return true;
                        const term = searchTerm.toLowerCase();
                        return (
                          (u.name || "").toLowerCase().includes(term) ||
                          (u.email || "").toLowerCase().includes(term) ||
                          (u.role || "").toLowerCase().includes(term) ||
                          (u.user_id || "").toLowerCase().includes(term)
                        );
                      })
                      .map(u => (
                      <TableRow key={u.user_id}>
                        <TableCell>
                          <div>
                            <p className="font-medium">{u.name}</p>
                            <p className="text-xs text-gray-400">{u.user_id?.slice(0, 15)}...</p>
                          </div>
                        </TableCell>
                        <TableCell>{u.email}</TableCell>
                        <TableCell><Badge variant="outline" className="capitalize">{u.role}</Badge></TableCell>
                        <TableCell>
                          <Badge className={
                            u.membership?.plan === "platinum" ? "bg-purple-100 text-purple-800" :
                            u.membership?.plan === "premium" ? "bg-blue-100 text-blue-800" :
                            "bg-gray-100 text-gray-800"
                          }>
                            {u.membership?.plan || "free"}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {u.is_suspended ? (
                            <Badge className="bg-red-100 text-red-800">Suspended</Badge>
                          ) : (
                            <Badge className="bg-green-100 text-green-800">Active</Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button size="sm" variant="outline" className="h-7 px-2"
                              onClick={() => setUserActionDialog({ open: true, user: u, action: "reset_password", newPassword: "" })}>
                              <Key className="w-3 h-3" />
                            </Button>
                            {u.is_suspended ? (
                              <Button size="sm" variant="outline" className="h-7 px-2 text-green-600"
                                onClick={() => setUserActionDialog({ open: true, user: u, action: "unsuspend", newPassword: "" })}>
                                Unsuspend
                              </Button>
                            ) : (
                              <Button size="sm" variant="outline" className="h-7 px-2 text-yellow-600"
                                onClick={() => setUserActionDialog({ open: true, user: u, action: "suspend", newPassword: "" })}>
                                Suspend
                              </Button>
                            )}
                            <Button size="sm" variant="outline" className="h-7 px-2 text-red-600"
                              onClick={() => setUserActionDialog({ open: true, user: u, action: "delete", newPassword: "" })}>
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Subscriptions Tab */}
          <TabsContent value="subscriptions">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Subscription Management</CardTitle>
                <p className="text-sm text-gray-500">Manually upgrade, downgrade, or assign complimentary plans</p>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>User</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>Current Plan</TableHead>
                      <TableHead>Expires</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {users.filter(u => u.role !== "admin").map(u => (
                      <TableRow key={u.user_id}>
                        <TableCell className="font-medium">{u.name}</TableCell>
                        <TableCell>{u.email}</TableCell>
                        <TableCell><Badge variant="outline" className="capitalize">{u.role}</Badge></TableCell>
                        <TableCell>
                          <Badge className={`${u.membership?.plan === "promoter" ? "bg-gradient-to-r from-violet-500 to-pink-500 text-white" : u.membership?.plan === "platinum" ? "bg-amber-100 text-amber-800" : u.membership?.plan === "premium" ? "bg-violet-100 text-violet-800" : "bg-gray-100 text-gray-800"}`}>
                            {MEMBERSHIP_PLANS[u.membership?.plan]?.name || "Basic"}
                          </Badge>
                        </TableCell>
                        <TableCell>{u.membership?.expires_at ? new Date(u.membership.expires_at).toLocaleDateString() : "-"}</TableCell>
                        <TableCell>
                          <Button size="sm" variant="outline" className="h-7"
                            onClick={() => setSubscriptionDialog({ 
                              open: true, 
                              user: u, 
                              plan: u.membership?.plan || "basic",
                              expiresAt: u.membership?.expires_at || new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
                              note: "",
                              isComplimentary: false
                            })}>
                            <Edit className="w-3 h-3 mr-1" />Manage
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Advertisements Tab */}
          <TabsContent value="advertisements">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="text-base">Advertisements</CardTitle>
                  <p className="text-sm text-gray-500">Manage ads and track performance</p>
                </div>
                <Button size="sm" onClick={() => setAdDialog({ 
                  open: true, 
                  mode: "create",
                  ad: { title: "", description: "", image_url: "", redirect_url: "", placement: "homepage", ad_format: "banner", priority: 5, start_date: new Date().toISOString().split('T')[0], end_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], is_active: true }
                })}>
                  <Plus className="w-4 h-4 mr-1" />New Ad
                </Button>
              </CardHeader>
              <CardContent>
                {/* Ad Analytics Summary */}
                <div className="grid grid-cols-4 gap-4 mb-6">
                  <div className="text-center p-3 bg-violet-50 rounded-lg">
                    <p className="text-xl font-bold text-violet-600">{adAnalytics?.active_ads || 0}</p>
                    <p className="text-xs text-gray-500">Active</p>
                  </div>
                  <div className="text-center p-3 bg-blue-50 rounded-lg">
                    <p className="text-xl font-bold text-blue-600">{adAnalytics?.total_impressions || 0}</p>
                    <p className="text-xs text-gray-500">Impressions</p>
                  </div>
                  <div className="text-center p-3 bg-green-50 rounded-lg">
                    <p className="text-xl font-bold text-green-600">{adAnalytics?.total_clicks || 0}</p>
                    <p className="text-xs text-gray-500">Clicks</p>
                  </div>
                  <div className="text-center p-3 bg-pink-50 rounded-lg">
                    <p className="text-xl font-bold text-pink-600">{adAnalytics?.overall_ctr || 0}%</p>
                    <p className="text-xs text-gray-500">CTR</p>
                  </div>
                </div>

                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Title</TableHead>
                      <TableHead>Placement</TableHead>
                      <TableHead>Format</TableHead>
                      <TableHead>Impressions</TableHead>
                      <TableHead>Clicks</TableHead>
                      <TableHead>CTR</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {advertisements.map(ad => (
                      <TableRow key={ad.ad_id}>
                        <TableCell className="font-medium">{ad.title}</TableCell>
                        <TableCell><Badge variant="outline">{ad.placement}</Badge></TableCell>
                        <TableCell>{ad.ad_format}</TableCell>
                        <TableCell>{ad.impressions}</TableCell>
                        <TableCell>{ad.clicks}</TableCell>
                        <TableCell>{ad.ctr}%</TableCell>
                        <TableCell>
                          <Badge className={ad.is_active ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-800"}>
                            {ad.is_active ? "Active" : "Inactive"}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button size="sm" variant="ghost" className="h-7 w-7 p-0"
                              onClick={() => setAdDialog({ open: true, mode: "edit", ad })}>
                              <Edit className="w-3 h-3" />
                            </Button>
                            <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-600"
                              onClick={() => handleAdDelete(ad.ad_id)}>
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Ratings Tab */}
          <TabsContent value="ratings">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">All Ratings</CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Profile</TableHead>
                      <TableHead>Rating</TableHead>
                      <TableHead>Date</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {ratings.map((rating, i) => (
                      <TableRow key={i}>
                        <TableCell>{rating.profile_name}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            {[...Array(5)].map((_, j) => (
                              <Star key={j} className={`w-4 h-4 ${j < rating.rating ? "text-yellow-500 fill-yellow-500" : "text-gray-300"}`} />
                            ))}
                          </div>
                        </TableCell>
                        <TableCell>{new Date(rating.created_at).toLocaleDateString()}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Sub-Admins Tab */}
          <TabsContent value="subadmins">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="text-base">Sub-Admin Management</CardTitle>
                  <p className="text-sm text-gray-500">Create and manage sub-admin accounts with custom permissions</p>
                </div>
                <Button size="sm" onClick={openCreateSubAdminDialog} data-testid="create-subadmin-btn">
                  <Plus className="w-4 h-4 mr-1" />New Sub-Admin
                </Button>
              </CardHeader>
              <CardContent>
                {subAdmins.length === 0 ? (
                  <div className="text-center py-12">
                    <UserCog className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                    <p className="text-gray-500">No sub-admins created yet</p>
                    <p className="text-sm text-gray-400 mt-1">Create a sub-admin to delegate specific admin tasks</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Permissions</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Created</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {subAdmins.map(sa => (
                        <TableRow key={sa.user_id}>
                          <TableCell className="font-medium">{sa.name}</TableCell>
                          <TableCell className="text-gray-600">{sa.email}</TableCell>
                          <TableCell>
                            <div className="flex flex-wrap gap-1">
                              {sa.permissions?.profile_verification && <Badge variant="outline" className="text-xs">Profiles</Badge>}
                              {sa.permissions?.content_approval && <Badge variant="outline" className="text-xs">Content</Badge>}
                              {sa.permissions?.user_management && <Badge variant="outline" className="text-xs">Users</Badge>}
                              {sa.permissions?.subscription_management && <Badge variant="outline" className="text-xs">Subs</Badge>}
                              {sa.permissions?.ad_management && <Badge variant="outline" className="text-xs">Ads</Badge>}
                              {sa.permissions?.view_analytics && <Badge variant="outline" className="text-xs">Analytics</Badge>}
                              {!Object.values(sa.permissions || {}).some(v => v) && <span className="text-xs text-gray-400">No permissions</span>}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge className={sa.is_active !== false ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}>
                              {sa.is_active !== false ? "Active" : "Inactive"}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-gray-500 text-sm">{new Date(sa.created_at).toLocaleDateString()}</TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              <Button size="sm" variant="ghost" className="h-7 w-7 p-0" 
                                onClick={() => openEditSubAdminDialog(sa)}
                                data-testid={`edit-subadmin-${sa.user_id}`}>
                                <Edit className="w-3 h-3" />
                              </Button>
                              <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-600"
                                onClick={() => handleSubAdminDelete(sa.user_id)}
                                data-testid={`delete-subadmin-${sa.user_id}`}>
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Taxonomy Management Tab */}
          <TabsContent value="taxonomy">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Taxonomy Manager</CardTitle>
                  <Badge variant="outline">{taxonomy.total || 0} items</Badge>
                </div>
              </CardHeader>
              <CardContent>
                {/* Add New Taxonomy Item */}
                <div className="flex gap-3 mb-6 p-4 bg-gray-50 rounded-lg">
                  <select
                    value={newTaxonomyItem.type}
                    onChange={(e) => setNewTaxonomyItem({ ...newTaxonomyItem, type: e.target.value })}
                    className="border rounded-lg px-3 py-2 text-sm"
                  >
                    <option value="category">Category</option>
                    <option value="skill">Skill</option>
                    <option value="city">City</option>
                    <option value="company_type">Company Type</option>
                    <option value="trainer_category">Trainer Category</option>
                  </select>
                  <Input
                    placeholder="Enter value..."
                    value={newTaxonomyItem.value}
                    onChange={(e) => setNewTaxonomyItem({ ...newTaxonomyItem, value: e.target.value })}
                    className="flex-1"
                    onKeyDown={(e) => e.key === "Enter" && handleAddTaxonomy()}
                  />
                  <Button onClick={handleAddTaxonomy}>
                    <Plus className="w-4 h-4 mr-2" /> Add
                  </Button>
                </div>

                {/* Taxonomy List by Type */}
                {Object.entries(taxonomy.grouped || {}).map(([type, items]) => (
                  <div key={type} className="mb-6">
                    <h3 className="text-sm font-semibold text-gray-600 mb-2 uppercase flex items-center gap-2">
                      {type.replace(/_/g, " ")}
                      <Badge variant="secondary" className="text-xs">{items?.length || 0}</Badge>
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {(items || [])
                        .filter(item => !searchTerm || item.value?.toLowerCase().includes(searchTerm.toLowerCase()))
                        .slice(0, 50)
                        .map((item) => (
                        <div 
                          key={item.taxonomy_id}
                          className={`group flex items-center gap-1 px-3 py-1.5 rounded-full text-sm ${
                            item.status === "active" ? "bg-green-100 text-green-800" :
                            item.status === "inactive" ? "bg-gray-100 text-gray-600" :
                            "bg-yellow-100 text-yellow-800"
                          }`}
                        >
                          {editingTaxonomy === item.taxonomy_id ? (
                            <input
                              type="text"
                              defaultValue={item.value}
                              className="w-32 px-1 py-0.5 text-sm border rounded"
                              autoFocus
                              onBlur={(e) => {
                                if (e.target.value !== item.value) {
                                  handleUpdateTaxonomy(item.taxonomy_id, { value: e.target.value, type: item.type });
                                } else {
                                  setEditingTaxonomy(null);
                                }
                              }}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") e.target.blur();
                                if (e.key === "Escape") setEditingTaxonomy(null);
                              }}
                            />
                          ) : (
                            <>
                              <span>{item.value}</span>
                              <div className="hidden group-hover:flex items-center gap-1 ml-1">
                                <button
                                  onClick={() => setEditingTaxonomy(item.taxonomy_id)}
                                  className="p-0.5 hover:bg-white rounded"
                                >
                                  <Edit className="w-3 h-3" />
                                </button>
                                <button
                                  onClick={() => handleUpdateTaxonomy(item.taxonomy_id, { 
                                    status: item.status === "active" ? "inactive" : "active",
                                    type: item.type
                                  })}
                                  className="p-0.5 hover:bg-white rounded"
                                  title={item.status === "active" ? "Deactivate" : "Activate"}
                                >
                                  {item.status === "active" ? 
                                    <ToggleRight className="w-3 h-3 text-green-600" /> : 
                                    <ToggleLeft className="w-3 h-3 text-gray-400" />
                                  }
                                </button>
                                <button
                                  onClick={() => handleDeleteTaxonomy(item.taxonomy_id)}
                                  className="p-0.5 hover:bg-red-100 rounded text-red-500"
                                >
                                  <Trash2 className="w-3 h-3" />
                                </button>
                              </div>
                            </>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}

                {/* User-submitted items */}
                {taxonomy.items?.filter(i => i.created_by !== "system").length > 0 && (
                  <div className="border-t pt-4 mt-4">
                    <h3 className="text-sm font-semibold text-gray-600 mb-2">User-Submitted Items</h3>
                    <div className="flex flex-wrap gap-2">
                      {taxonomy.items
                        .filter(i => i.created_by !== "system")
                        .map((item) => (
                          <Badge 
                            key={item.taxonomy_id}
                            variant="outline"
                            className="flex items-center gap-1"
                          >
                            <span className="text-xs text-gray-400">{item.type}:</span>
                            {item.value}
                          </Badge>
                        ))
                      }
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Announcements Tab */}
          <TabsContent value="announcements">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <Megaphone className="w-5 h-5" />
                    Announcements Management
                  </CardTitle>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline">Pending: {announcementCounts.pending}</Badge>
                    <Badge variant="secondary">Published: {announcementCounts.published}</Badge>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {/* Filter buttons */}
                <div className="flex gap-2 mb-4">
                  <Button 
                    variant={announcementFilter === "all" ? "default" : "outline"} 
                    size="sm"
                    onClick={() => setAnnouncementFilter("all")}
                  >
                    All ({announcements.length})
                  </Button>
                  <Button 
                    variant={announcementFilter === "pending" ? "default" : "outline"} 
                    size="sm"
                    onClick={() => setAnnouncementFilter("pending")}
                  >
                    Pending ({announcementCounts.pending})
                  </Button>
                  <Button 
                    variant={announcementFilter === "published" ? "default" : "outline"} 
                    size="sm"
                    onClick={() => setAnnouncementFilter("published")}
                  >
                    Published ({announcementCounts.published})
                  </Button>
                  <Button 
                    variant={announcementFilter === "rejected" ? "default" : "outline"} 
                    size="sm"
                    onClick={() => setAnnouncementFilter("rejected")}
                  >
                    Rejected ({announcementCounts.rejected})
                  </Button>
                </div>

                {/* Announcements table */}
                {filteredAnnouncements.length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Title</TableHead>
                        <TableHead>Author</TableHead>
                        <TableHead>Role</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Created</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredAnnouncements.map(ann => (
                        <TableRow key={ann.announcement_id}>
                          <TableCell>
                            <div>
                              <p className="font-medium">{ann.title}</p>
                              <p className="text-xs text-gray-500 line-clamp-1">{ann.content}</p>
                            </div>
                          </TableCell>
                          <TableCell>{ann.author_name}</TableCell>
                          <TableCell>
                            <Badge variant="outline" className="capitalize">{ann.author_role}</Badge>
                          </TableCell>
                          <TableCell>
                            <Badge variant="secondary" className="capitalize">{ann.announcement_type}</Badge>
                          </TableCell>
                          <TableCell>
                            <Badge 
                              variant={ann.status === "published" ? "default" : ann.status === "pending" ? "outline" : "destructive"}
                              className="capitalize"
                            >
                              {ann.status}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-sm text-gray-500">
                            {new Date(ann.created_at).toLocaleDateString()}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-1">
                              {ann.status !== "published" && (
                                <Button 
                                  size="sm" 
                                  variant="outline" 
                                  className="text-green-600 hover:text-green-700"
                                  onClick={() => handlePublishAnnouncement(ann.announcement_id)}
                                >
                                  <CheckCircle className="w-4 h-4" />
                                </Button>
                              )}
                              {ann.status === "published" && (
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  onClick={() => handleUnpublishAnnouncement(ann.announcement_id)}
                                >
                                  <XCircle className="w-4 h-4" />
                                </Button>
                              )}
                              <Button 
                                size="sm" 
                                variant="outline" 
                                className="text-red-600 hover:text-red-700"
                                onClick={() => handleDeleteAnnouncement(ann.announcement_id)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <Megaphone className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p>No announcements found</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* SETTINGS TAB */}
          <TabsContent value="settings">
            <div className="space-y-6">
              {/* WhatsApp Settings Card */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <svg className="w-5 h-5 text-green-500" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                    </svg>
                    WhatsApp Support Settings
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="text-base font-medium">Enable WhatsApp Button</Label>
                      <p className="text-sm text-gray-500">Show floating WhatsApp support button on site</p>
                    </div>
                    <Switch
                      checked={platformSettings.whatsapp_enabled}
                      onCheckedChange={(checked) => setPlatformSettings(prev => ({ ...prev, whatsapp_enabled: checked }))}
                      data-testid="whatsapp-enabled-toggle"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="whatsapp-number">WhatsApp Contact Number</Label>
                    <div className="flex gap-2">
                      <span className="flex items-center px-3 bg-gray-100 border border-r-0 rounded-l-md text-gray-500">+</span>
                      <Input
                        id="whatsapp-number"
                        placeholder="Enter number with country code (e.g., 919876543210)"
                        value={platformSettings.whatsapp_number}
                        onChange={(e) => {
                          // Only allow digits
                          const value = e.target.value.replace(/\D/g, '');
                          setPlatformSettings(prev => ({ ...prev, whatsapp_number: value }));
                        }}
                        className="rounded-l-none"
                        data-testid="whatsapp-number-input"
                      />
                    </div>
                    <p className="text-xs text-gray-500">Enter only digits including country code. Example: 88866065375</p>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="whatsapp-message">Default Message</Label>
                    <Textarea
                      id="whatsapp-message"
                      placeholder="Pre-filled message when user clicks WhatsApp button"
                      value={platformSettings.whatsapp_default_message}
                      onChange={(e) => setPlatformSettings(prev => ({ ...prev, whatsapp_default_message: e.target.value }))}
                      rows={2}
                      data-testid="whatsapp-message-input"
                    />
                  </div>

                  {platformSettings.whatsapp_number && (
                    <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                      <p className="text-sm text-green-800">
                        <strong>Preview Link:</strong>{" "}
                        <a 
                          href={`https://wa.me/${platformSettings.whatsapp_number}`} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="underline hover:text-green-600"
                        >
                          https://wa.me/{platformSettings.whatsapp_number}
                        </a>
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* General Platform Settings Card */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Settings className="w-5 h-5 text-gray-500" />
                    General Platform Settings
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="support-email">Support Email</Label>
                    <Input
                      id="support-email"
                      type="email"
                      placeholder="support@talentmeet.in"
                      value={platformSettings.support_email}
                      onChange={(e) => setPlatformSettings(prev => ({ ...prev, support_email: e.target.value }))}
                      data-testid="support-email-input"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="custom-domain">Custom Domain URL</Label>
                    <Input
                      id="custom-domain"
                      placeholder="https://www.talentmeet.in"
                      value={platformSettings.custom_domain}
                      onChange={(e) => setPlatformSettings(prev => ({ ...prev, custom_domain: e.target.value }))}
                      data-testid="custom-domain-input"
                    />
                    <p className="text-xs text-gray-500">Used for generating share links</p>
                  </div>
                </CardContent>
              </Card>

              {/* Save Button */}
              <div className="flex justify-end">
                <Button 
                  onClick={handleSavePlatformSettings}
                  disabled={savingSettings}
                  className="px-8"
                  data-testid="save-settings-btn"
                >
                  {savingSettings ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Save Settings
                    </>
                  )}
                </Button>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </main>

      {/* Profile Action Dialog */}
      <Dialog open={profileDialog.open} onOpenChange={(open) => !open && setProfileDialog({ ...profileDialog, open: false })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="capitalize">{profileDialog.action} Profile</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="mb-4">
              {profileDialog.action === "verify" && "Are you sure you want to verify this profile? It will display a verified badge."}
              {profileDialog.action === "reject" && "Please provide a reason for rejection:"}
              {profileDialog.action === "revoke" && "Please provide a reason for revoking verification:"}
            </p>
            {(profileDialog.action === "reject" || profileDialog.action === "revoke") && (
              <Textarea
                placeholder="Enter reason..."
                value={profileDialog.reason}
                onChange={(e) => setProfileDialog({ ...profileDialog, reason: e.target.value })}
              />
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setProfileDialog({ ...profileDialog, open: false })}>Cancel</Button>
            <Button onClick={handleProfileAction} className={profileDialog.action === "verify" ? "bg-green-600" : "bg-red-600"}>
              {profileDialog.action === "verify" ? "Verify" : profileDialog.action === "reject" ? "Reject" : "Revoke"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Subscription Dialog */}
      <Dialog open={subscriptionDialog.open} onOpenChange={(open) => !open && setSubscriptionDialog({ ...subscriptionDialog, open: false })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Manage Subscription - {subscriptionDialog.user?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Plan</Label>
              <Select value={subscriptionDialog.plan} onValueChange={(v) => setSubscriptionDialog({ ...subscriptionDialog, plan: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(MEMBERSHIP_PLANS).map(([id, p]) => (
                    <SelectItem key={id} value={id}>{p.name} - ₹{p.price.toLocaleString()}/{p.period}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Expires At</Label>
              <Input type="date" value={subscriptionDialog.expiresAt?.split('T')[0]} onChange={(e) => setSubscriptionDialog({ ...subscriptionDialog, expiresAt: e.target.value })} />
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={subscriptionDialog.isComplimentary} onCheckedChange={(v) => setSubscriptionDialog({ ...subscriptionDialog, isComplimentary: v })} />
              <Label>Complimentary / Promotional</Label>
            </div>
            <div>
              <Label>Admin Note</Label>
              <Textarea placeholder="Add a note..." value={subscriptionDialog.note} onChange={(e) => setSubscriptionDialog({ ...subscriptionDialog, note: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSubscriptionDialog({ ...subscriptionDialog, open: false })}>Cancel</Button>
            <Button onClick={handleSubscriptionUpdate}>Update Subscription</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Advertisement Dialog */}
      <Dialog open={adDialog.open} onOpenChange={(open) => !open && setAdDialog({ ...adDialog, open: false })}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{adDialog.mode === "create" ? "Create" : "Edit"} Advertisement</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
            <div>
              <Label>Title</Label>
              <Input value={adDialog.ad?.title || ""} onChange={(e) => setAdDialog({ ...adDialog, ad: { ...adDialog.ad, title: e.target.value }})} />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea value={adDialog.ad?.description || ""} onChange={(e) => setAdDialog({ ...adDialog, ad: { ...adDialog.ad, description: e.target.value }})} />
            </div>
            <div>
              <Label>Banner Image</Label>
              <input 
                type="file" 
                ref={adImageRef} 
                className="hidden" 
                accept="image/jpeg,image/png,image/webp,image/gif"
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  
                  setUploadingImage(true);
                  const token = localStorage.getItem("token");
                  const formData = new FormData();
                  formData.append("file", file);
                  formData.append("file_type", "photo");
                  
                  try {
                    const response = await fetch(`${API_URL}/api/upload`, {
                      method: "POST",
                      headers: { Authorization: `Bearer ${token}` },
                      body: formData
                    });
                    if (response.ok) {
                      const data = await response.json();
                      const imageUrl = `${API_URL}${data.url}`;
                      setAdDialog({ ...adDialog, ad: { ...adDialog.ad, image_url: imageUrl }});
                      toast.success("Image uploaded!");
                    } else {
                      toast.error("Upload failed");
                    }
                  } catch (error) {
                    toast.error("Upload failed");
                  } finally {
                    setUploadingImage(false);
                  }
                }}
              />
              <div className="mt-2">
                {adDialog.ad?.image_url ? (
                  <div className="relative">
                    <img 
                      src={adDialog.ad.image_url} 
                      alt="Banner preview" 
                      className="w-full h-32 object-cover rounded-lg border"
                    />
                    <Button 
                      size="sm" 
                      variant="destructive" 
                      className="absolute top-2 right-2"
                      onClick={() => setAdDialog({ ...adDialog, ad: { ...adDialog.ad, image_url: "" }})}
                    >
                      <X className="w-3 h-3" />
                    </Button>
                  </div>
                ) : (
                  <Button 
                    variant="outline" 
                    className="w-full h-32 border-dashed"
                    onClick={() => adImageRef.current?.click()}
                    disabled={uploadingImage}
                  >
                    {uploadingImage ? (
                      <Loader2 className="w-6 h-6 animate-spin" />
                    ) : (
                      <div className="text-center">
                        <Upload className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                        <span className="text-sm text-gray-500">Click to upload banner image</span>
                      </div>
                    )}
                  </Button>
                )}
              </div>
              <p className="text-xs text-gray-500 mt-1">Supports JPEG, PNG, WebP, GIF. Will auto-resize to fit.</p>
            </div>
            <div>
              <Label>Redirect URL</Label>
              <Input value={adDialog.ad?.redirect_url || ""} onChange={(e) => setAdDialog({ ...adDialog, ad: { ...adDialog.ad, redirect_url: e.target.value }})} placeholder="https://..." />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Placement</Label>
                <Select value={adDialog.ad?.placement} onValueChange={(v) => setAdDialog({ ...adDialog, ad: { ...adDialog.ad, placement: v }})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {AD_PLACEMENTS.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Format</Label>
                <Select value={adDialog.ad?.ad_format} onValueChange={(v) => setAdDialog({ ...adDialog, ad: { ...adDialog.ad, ad_format: v }})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {AD_FORMATS.map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Start Date</Label>
                <Input type="date" value={adDialog.ad?.start_date?.split('T')[0]} onChange={(e) => setAdDialog({ ...adDialog, ad: { ...adDialog.ad, start_date: e.target.value }})} />
              </div>
              <div>
                <Label>End Date</Label>
                <Input type="date" value={adDialog.ad?.end_date?.split('T')[0]} onChange={(e) => setAdDialog({ ...adDialog, ad: { ...adDialog.ad, end_date: e.target.value }})} />
              </div>
            </div>
            <div>
              <Label>Priority (1-10)</Label>
              <Input type="number" min="1" max="10" value={adDialog.ad?.priority || 5} onChange={(e) => setAdDialog({ ...adDialog, ad: { ...adDialog.ad, priority: parseInt(e.target.value) }})} />
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={adDialog.ad?.is_active} onCheckedChange={(v) => setAdDialog({ ...adDialog, ad: { ...adDialog.ad, is_active: v }})} />
              <Label>Active</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAdDialog({ ...adDialog, open: false })}>Cancel</Button>
            <Button onClick={handleAdSave}>{adDialog.mode === "create" ? "Create" : "Update"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Content Action Dialog */}
      <Dialog open={contentDialog.open} onOpenChange={(open) => !open && setContentDialog({ ...contentDialog, open: false })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="capitalize">{contentDialog.action} Content</DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div>
              <p className="font-medium">{contentDialog.content?.title}</p>
              <p className="text-sm text-gray-500">{contentDialog.content?.content_type?.replace("_", " ")} by {contentDialog.content?.creator_name}</p>
            </div>
            {contentDialog.content?.description && (
              <p className="text-sm bg-gray-50 p-3 rounded">{contentDialog.content?.description}</p>
            )}
            {contentDialog.action === "reject" && (
              <div>
                <Label className="text-red-600">Rejection Reason (Required)</Label>
                <Textarea 
                  placeholder="Explain why this content is being rejected..." 
                  value={contentDialog.reason} 
                  onChange={(e) => setContentDialog({ ...contentDialog, reason: e.target.value })}
                  className="mt-2"
                />
                <p className="text-xs text-gray-500 mt-1">This will be visible to the creator for revision</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setContentDialog({ ...contentDialog, open: false })}>Cancel</Button>
            <Button 
              onClick={handleContentAction} 
              className={contentDialog.action === "approve" ? "bg-green-600" : "bg-red-600"}
              disabled={contentDialog.action === "reject" && !contentDialog.reason?.trim()}
            >
              {contentDialog.action === "approve" ? "Approve" : "Reject"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sub-Admin Dialog */}
      <Dialog open={subAdminDialog.open} onOpenChange={(open) => !open && setSubAdminDialog({ ...subAdminDialog, open: false })}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{subAdminDialog.mode === "create" ? "Create Sub-Admin" : "Edit Sub-Admin"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
            {subAdminDialog.mode === "create" && (
              <>
                <div>
                  <Label>Email</Label>
                  <Input 
                    type="email" 
                    placeholder="subadmin@talentmeet.com"
                    value={subAdminDialog.formData.email} 
                    onChange={(e) => setSubAdminDialog({ 
                      ...subAdminDialog, 
                      formData: { ...subAdminDialog.formData, email: e.target.value }
                    })}
                    data-testid="subadmin-email-input"
                  />
                </div>
                <div>
                  <Label>Password</Label>
                  <Input 
                    type="password" 
                    placeholder="••••••••"
                    value={subAdminDialog.formData.password} 
                    onChange={(e) => setSubAdminDialog({ 
                      ...subAdminDialog, 
                      formData: { ...subAdminDialog.formData, password: e.target.value }
                    })}
                    data-testid="subadmin-password-input"
                  />
                </div>
              </>
            )}
            <div>
              <Label>Name</Label>
              <Input 
                placeholder="Sub-Admin Name"
                value={subAdminDialog.formData.name} 
                onChange={(e) => setSubAdminDialog({ 
                  ...subAdminDialog, 
                  formData: { ...subAdminDialog.formData, name: e.target.value }
                })}
                data-testid="subadmin-name-input"
              />
            </div>
            
            <div className="space-y-3">
              <Label className="text-base font-medium">Permissions</Label>
              <div className="space-y-3 bg-gray-50 p-4 rounded-lg">
                {[
                  { key: "profile_verification", label: "Profile Verification", desc: "Approve/reject profiles" },
                  { key: "content_approval", label: "Content Approval", desc: "Approve jobs, meetings, auditions" },
                  { key: "user_management", label: "User Management", desc: "View and manage users" },
                  { key: "subscription_management", label: "Subscription Control", desc: "Manage user subscriptions" },
                  { key: "ad_management", label: "Ad Management", desc: "Create and manage ads" },
                  { key: "view_analytics", label: "View Analytics", desc: "Access analytics dashboard" },
                  { key: "view_ratings", label: "View Ratings", desc: "View user ratings" }
                ].map((perm) => (
                  <div key={perm.key} className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium">{perm.label}</p>
                      <p className="text-xs text-gray-500">{perm.desc}</p>
                    </div>
                    <Switch
                      checked={subAdminDialog.formData.permissions[perm.key]}
                      onCheckedChange={(checked) => setSubAdminDialog({
                        ...subAdminDialog,
                        formData: {
                          ...subAdminDialog.formData,
                          permissions: { ...subAdminDialog.formData.permissions, [perm.key]: checked }
                        }
                      })}
                      data-testid={`subadmin-perm-${perm.key}`}
                    />
                  </div>
                ))}
              </div>
            </div>

            {subAdminDialog.mode === "edit" && (
              <div className="flex items-center justify-between pt-2 border-t">
                <div>
                  <p className="text-sm font-medium">Account Status</p>
                  <p className="text-xs text-gray-500">Enable or disable this sub-admin</p>
                </div>
                <Switch
                  checked={subAdminDialog.formData.is_active !== false}
                  onCheckedChange={(checked) => setSubAdminDialog({
                    ...subAdminDialog,
                    formData: { ...subAdminDialog.formData, is_active: checked }
                  })}
                  data-testid="subadmin-active-toggle"
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSubAdminDialog({ ...subAdminDialog, open: false })}>Cancel</Button>
            <Button onClick={handleSubAdminSave} data-testid="subadmin-save-btn">
              {subAdminDialog.mode === "create" ? "Create Sub-Admin" : "Update Sub-Admin"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Password Change Dialog */}
      <Dialog open={passwordDialog.open} onOpenChange={(open) => !open && setPasswordDialog({ ...passwordDialog, open: false })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change Admin Password</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Current Password</Label>
              <Input 
                type="password" 
                value={passwordDialog.currentPassword}
                onChange={(e) => setPasswordDialog({ ...passwordDialog, currentPassword: e.target.value })}
              />
            </div>
            <div>
              <Label>New Password</Label>
              <Input 
                type="password" 
                value={passwordDialog.newPassword}
                onChange={(e) => setPasswordDialog({ ...passwordDialog, newPassword: e.target.value })}
              />
            </div>
            <div>
              <Label>Confirm New Password</Label>
              <Input 
                type="password" 
                value={passwordDialog.confirmPassword}
                onChange={(e) => setPasswordDialog({ ...passwordDialog, confirmPassword: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPasswordDialog({ ...passwordDialog, open: false })}>Cancel</Button>
            <Button onClick={handlePasswordChange}>Change Password</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* User Action Dialog */}
      <Dialog open={userActionDialog.open} onOpenChange={(open) => !open && setUserActionDialog({ ...userActionDialog, open: false })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="capitalize">{userActionDialog.action?.replace("_", " ")} User</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="mb-4">User: <strong>{userActionDialog.user?.name}</strong> ({userActionDialog.user?.email})</p>
            
            {userActionDialog.action === "reset_password" && (
              <div>
                <Label>New Password</Label>
                <Input 
                  type="password"
                  placeholder="Minimum 8 characters"
                  value={userActionDialog.newPassword}
                  onChange={(e) => setUserActionDialog({ ...userActionDialog, newPassword: e.target.value })}
                />
                <p className="text-xs text-gray-500 mt-1">User will be required to change password on next login</p>
              </div>
            )}
            
            {userActionDialog.action === "delete" && (
              <p className="text-red-600 text-sm">⚠️ This action cannot be undone. All user data, profiles, and content will be permanently deleted.</p>
            )}
            
            {userActionDialog.action === "suspend" && (
              <p className="text-yellow-600 text-sm">This will suspend the user&apos;s account and prevent them from logging in.</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUserActionDialog({ ...userActionDialog, open: false })}>Cancel</Button>
            <Button 
              onClick={handleUserAction}
              className={userActionDialog.action === "delete" ? "bg-red-600" : userActionDialog.action === "suspend" ? "bg-yellow-600" : ""}
              disabled={userActionDialog.action === "reset_password" && (!userActionDialog.newPassword || userActionDialog.newPassword.length < 8)}
            >
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create User Dialog */}
      <Dialog open={createUserDialog.open} onOpenChange={(open) => !open && setCreateUserDialog({ ...createUserDialog, open: false })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New User</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Name</Label>
              <Input 
                placeholder="Full name"
                value={createUserDialog.name}
                onChange={(e) => setCreateUserDialog({ ...createUserDialog, name: e.target.value })}
              />
            </div>
            <div>
              <Label>Email</Label>
              <Input 
                type="email"
                placeholder="user@example.com"
                value={createUserDialog.email}
                onChange={(e) => setCreateUserDialog({ ...createUserDialog, email: e.target.value })}
              />
            </div>
            <div>
              <Label>Password</Label>
              <Input 
                type="password"
                placeholder="Minimum 8 characters"
                value={createUserDialog.password}
                onChange={(e) => setCreateUserDialog({ ...createUserDialog, password: e.target.value })}
              />
            </div>
            <div>
              <Label>Role</Label>
              <Select value={createUserDialog.role} onValueChange={(v) => setCreateUserDialog({ ...createUserDialog, role: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="talent">Talent</SelectItem>
                  <SelectItem value="trainer">Trainer</SelectItem>
                  <SelectItem value="enterprise">Enterprise</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateUserDialog({ ...createUserDialog, open: false })}>Cancel</Button>
            <Button onClick={handleCreateUser}>Create User</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
